import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Card, CardContent, TextField, Chip, Button, Switch, FormControlLabel,
    Divider, Typography, Tooltip, Tabs, Tab,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { RichTextEditor } from '../Requests/RichText';
import { getProviderInfo, saveProviderInfo } from '../../../services/providerService';
import { currentUserName } from '../../../services/integrationHubService';
import DatasetsList from './DatasetsList';
import KeyContacts from './KeyContacts';
import LinkList from './LinkList';
import Attachments from './Attachments';

const BLUE = '#004df2';

const SAVE_DEBOUNCE_MS = 1500;

/** Raw backend/DB errors aren't for district-facing eyes — log the real one, show a plain one. */
const FRIENDLY_ERROR = "Couldn't load this provider's info right now. Try refreshing — if it keeps happening, let the Nexus team know.";
function friendlyError(e) {
    // eslint-disable-next-line no-console
    console.error('ProviderInfoCard:', e);
    return FRIENDLY_ERROR;
}

const EMPTY_INFO = {
    category: '', whatItIs: '', datasets: [], segmentFlagsDisabled: false,
    contacts: [], notesHtml: '', links: [], outreachHtml: '',
};

const TABS = [
    { id: 'about', label: 'About', icon: <InfoOutlinedIcon sx={{ fontSize: 16 }} /> },
    { id: 'contacts', label: 'Contacts', icon: <PersonOutlineIcon sx={{ fontSize: 16 }} /> },
    { id: 'notes', label: 'Notes', icon: <StickyNote2OutlinedIcon sx={{ fontSize: 16 }} /> },
    { id: 'outreach', label: 'Outreach', icon: <MailOutlineIcon sx={{ fontSize: 16 }} /> },
];

// Everything about THIS PROVIDER, shared across every client that has it —
// Category/What-it-is/Data-we-pull describe the data; Contacts/Notes/Links/
// Files are how Nexus generally works with this vendor; Outreach is the
// canned language for kicking off a new client. None of this is per-client —
// per-client nuance (this district's own connection, their live segments)
// stays on that client's own Schema/Notes tabs instead.

const ProviderInfoCard = ({ providerId, onSegmentFlagsChange }) => {
    const [tab, setTab] = useState('about');
    const [info, setInfo] = useState(EMPTY_INFO);
    const [updatedOn, setUpdatedOn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveState, setSaveState] = useState('idle');
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState(null);

    const infoRef = useRef(info);
    infoRef.current = info;
    const updatedOnRef = useRef(null);
    updatedOnRef.current = updatedOn;
    const saveTimer = useRef(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setSaveState('idle');
        getProviderInfo(providerId)
            .then((d) => {
                if (!alive) return;
                setInfo({
                    category: d.category || '', whatItIs: d.whatItIs || '',
                    datasets: d.datasets || [], segmentFlagsDisabled: !!d.segmentFlagsDisabled,
                    contacts: d.contacts || [], notesHtml: d.notesHtml || '',
                    links: d.links || [], outreachHtml: d.outreachHtml || '',
                });
                setUpdatedOn(d.updatedOn || null);
                onSegmentFlagsChange?.(!!d.segmentFlagsDisabled);
            })
            .catch((e) => { if (alive) setError(friendlyError(e)); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; clearTimeout(saveTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerId]);

    const persist = useCallback(async () => {
        setSaveState('saving');
        try {
            const who = await currentUserName();
            const res = await saveProviderInfo(providerId, infoRef.current, who, updatedOnRef.current);
            setUpdatedOn(res.updatedOn || null);
            setDirty(false);
            setSaveState('saved');
        } catch (e) {
            setError(friendlyError(e));
            setSaveState('error');
        }
    }, [providerId]);

    const update = (next) => {
        setInfo(next);
        setDirty(true);
        if (next.segmentFlagsDisabled !== info.segmentFlagsDisabled) onSegmentFlagsChange?.(next.segmentFlagsDisabled);
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(persist, SAVE_DEBOUNCE_MS);
    };

    const saveNow = () => {
        clearTimeout(saveTimer.current);
        persist();
    };

    if (loading) return null;

    return (
        <Card variant="outlined" sx={{ borderRadius: 3, mb: 2.5 }}>
            <CardContent>
                {error && <Typography sx={{ color: '#c62828', fontSize: 12.5, mb: 1 }}>{error}</Typography>}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        sx={{
                            minHeight: 36,
                            flex: 1,
                            '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontSize: 12.5, fontWeight: 600, gap: 0.6, minWidth: 0, px: 1.5 },
                            '& .Mui-selected': { color: BLUE },
                        }}
                        TabIndicatorProps={{ style: { backgroundColor: BLUE, height: 2, borderRadius: 2 } }}
                    >
                        {TABS.map((t) => (
                            <Tab key={t.id} value={t.id} label={t.label} iconPosition="start" icon={t.icon} />
                        ))}
                    </Tabs>
                    {saveState !== 'idle' && (
                        <Chip
                            size="small"
                            label={saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Not saved' : 'Saved'}
                            color={saveState === 'error' ? 'error' : saveState === 'saved' ? 'success' : 'default'}
                            variant={saveState === 'saving' ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 600, fontSize: 10.5, height: 20, mr: 1 }}
                        />
                    )}
                    <Button size="small" onClick={saveNow} disabled={!dirty || saveState === 'saving'}
                        sx={{ textTransform: 'none', fontSize: 11.5, color: BLUE, minWidth: 0, py: 0.25 }}>
                        Save
                    </Button>
                </Box>

                {tab === 'about' && (
                    <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                                size="small" label="Category" placeholder="e.g. SIS, Assessment, LMS" sx={{ maxWidth: 260 }}
                                value={info.category} onChange={(e) => update({ ...info, category: e.target.value })}
                            />
                            <TextField
                                size="small" label="What it is" fullWidth multiline minRows={2} maxRows={12}
                                placeholder="What this provider is and what data it pulls"
                                value={info.whatItIs} onChange={(e) => update({ ...info, whatItIs: e.target.value })}
                            />
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <DatasetsList datasets={info.datasets} onChange={(datasets) => update({ ...info, datasets })} />

                        <Divider sx={{ my: 2 }} />

                        <Tooltip
                            arrow placement="top"
                            title="When on, each client in the list below gets flagged if their live segments differ from what other clients on this provider have — a hint, not an alert. Turn it off for a provider that's supposed to vary client-to-client (e.g. state-specific fields)."
                        >
                            <FormControlLabel
                                sx={{ ml: 0 }}
                                control={(
                                    <Switch
                                        size="small" checked={!info.segmentFlagsDisabled}
                                        onChange={(e) => update({ ...info, segmentFlagsDisabled: !e.target.checked })}
                                    />
                                )}
                                label={<Typography sx={{ fontSize: 12.5 }}>Flag segment differences across clients</Typography>}
                            />
                        </Tooltip>
                    </>
                )}

                {tab === 'contacts' && (
                    <KeyContacts contacts={info.contacts} onChange={(contacts) => update({ ...info, contacts })} />
                )}

                {tab === 'notes' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.75 }}>
                                Notes for this provider — connection details, quirks, account info, whatever the next
                                person needs to know. Shared across every client that has it.
                            </Typography>
                            <RichTextEditor value={info.notesHtml} onChange={(html) => update({ ...info, notesHtml: html })} />
                        </Box>
                        <Attachments providerId={providerId} />
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5, letterSpacing: 0.3 }}>LINKS</Typography>
                            <LinkList links={info.links} addLabel="Add link" onChange={(links) => update({ ...info, links })} />
                        </Box>
                    </Box>
                )}

                {tab === 'outreach' && (
                    <Box>
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.75 }}>
                            Boilerplate outreach language for this provider — what we typically say when asking a
                            district to set this integration up. Copy/paste into an email and fill in the specifics.
                        </Typography>
                        <RichTextEditor value={info.outreachHtml} onChange={(html) => update({ ...info, outreachHtml: html })} />
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default ProviderInfoCard;
